import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  VStack,
  Alert,
  AlertIcon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormHelperText,
  SimpleGrid,
  Image,
  Box,
  Text,
  Spinner,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { serviceApi, visitApi, getStaticFileUrl, UploadsService } from '../services/api';

function VisitModal({ isOpen, onClose, patientId, patientName, visitToEdit, onSave }) {
  const toast = useToast();
  const isEdit = Boolean(visitToEdit);

  const [formData, setFormData] = useState({
    patient_id: patientId,
    service_id: '',
    visit_date: '',
    quantity: 1,
    notes: '',
    before_image_url: '',
    after_image_url: '',
  });
  const [services, setServices] = useState([]);
  const [beforeImagePreview, setBeforeImagePreview] = useState(null);
  const [afterImagePreview, setAfterImagePreview] = useState(null);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      if (visitToEdit) {
        setFormData(visitToEdit);
        if (visitToEdit.before_image_url) {
          setBeforeImagePreview(getStaticFileUrl(visitToEdit.before_image_url));
        }
        if (visitToEdit.after_image_url) {
          setAfterImagePreview(getStaticFileUrl(visitToEdit.after_image_url));
        }
      } else {
        // Reset form for new visit
        setFormData({
          patient_id: patientId,
          service_id: '',
          visit_date: new Date().toISOString().split('T')[0], // Default to today
          quantity: 1,
          notes: '',
          before_image_url: '',
          after_image_url: '',
        });
        setBeforeImagePreview(null);
        setAfterImagePreview(null);
      }
    }
  }, [isOpen, visitToEdit, patientId]);

  const fetchServices = async () => {
    try {
      const response = await serviceApi.getAll(true); // Only active services
      setServices(response.data);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    try {
      if (type === 'before') setUploadingBefore(true);
      if (type === 'after') setUploadingAfter(true);

      const data = await UploadsService.uploadImageApiUploadsUploadPost({
        formData: { file },
      });

      setFormData((prev) => ({
        ...prev,
        [type === 'before' ? 'before_image_url' : 'after_image_url']: data.url,
      }));

      if (type === 'before') {
        setBeforeImagePreview(URL.createObjectURL(file));
      } else {
        setAfterImagePreview(URL.createObjectURL(file));
      }

      toast({
        title: `${type === 'before' ? 'Before' : 'After'} photo uploaded`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: `Failed to upload ${type} image`,
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      console.error(err);
    } finally {
      if (type === 'before') setUploadingBefore(false);
      if (type === 'after') setUploadingAfter(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await visitApi.update(visitToEdit.id, formData);
        toast({
          title: 'Visit updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await visitApi.create(formData);
        toast({
          title: 'Visit recorded successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      if (onSave) onSave(); // Refresh the visit list
      onClose();
    } catch (err) {
      setError(isEdit ? 'Failed to update visit' : 'Failed to create visit');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEdit ? 'Edit Visit' : 'Record New Visit'} - {patientName}
        </ModalHeader>
        <ModalCloseButton />

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && (
              <Alert status="error" borderRadius="md" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <VStack spacing={5} align="stretch">
              <FormControl isRequired>
                <FormLabel>Service</FormLabel>
                <Select
                  name="service_id"
                  value={formData.service_id}
                  onChange={handleChange}
                  placeholder="Select a service"
                >
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.name} - ${parseFloat(service.price).toFixed(2)}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Visit Date</FormLabel>
                <Input
                  type="date"
                  name="visit_date"
                  value={formData.visit_date}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Quantity/Units</FormLabel>
                <NumberInput
                  min={1}
                  value={formData.quantity}
                  onChange={(valueString) =>
                    setFormData((prev) => ({ ...prev, quantity: parseInt(valueString) || 1 }))
                  }
                >
                  <NumberInputField placeholder="1" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>e.g., units of Botox, number of syringes</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Visit details, areas treated, observations, etc."
                />
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Before Photo</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'before')}
                    isDisabled={uploadingBefore}
                    pt={1}
                    size="sm"
                  />
                  {uploadingBefore && (
                    <Flex align="center" gap={2} mt={2}>
                      <Spinner size="sm" color="brand.purple" />
                      <Text fontSize="sm" color="brand.purple">
                        Uploading...
                      </Text>
                    </Flex>
                  )}
                  {beforeImagePreview && (
                    <Box mt={3}>
                      <Image
                        src={beforeImagePreview}
                        alt="Before"
                        borderRadius="md"
                        boxShadow="sm"
                        w="full"
                        maxH="200px"
                        objectFit="cover"
                      />
                    </Box>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel>After Photo</FormLabel>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'after')}
                    isDisabled={uploadingAfter}
                    pt={1}
                    size="sm"
                  />
                  {uploadingAfter && (
                    <Flex align="center" gap={2} mt={2}>
                      <Spinner size="sm" color="brand.purple" />
                      <Text fontSize="sm" color="brand.purple">
                        Uploading...
                      </Text>
                    </Flex>
                  )}
                  {afterImagePreview && (
                    <Box mt={3}>
                      <Image
                        src={afterImagePreview}
                        alt="After"
                        borderRadius="md"
                        boxShadow="sm"
                        w="full"
                        maxH="200px"
                        objectFit="cover"
                      />
                    </Box>
                  )}
                </FormControl>
              </SimpleGrid>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="purple"
              isLoading={loading}
              loadingText="Saving"
            >
              {isEdit ? 'Update Visit' : 'Record Visit'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}

export default VisitModal;
